/**
 * `resolveRoomAmbience` (R35) — what the active room sounds like right now.
 *
 * @remarks
 * A pure derived read, the `resolveRoomImage` precedent exactly: nothing is
 * written, nothing is stamped, the transcript never learns a sound existed,
 * and a re-render re-derives the same answer from the same state. Conditions
 * evaluate through the same `evaluateCondition` as everything else, so sound
 * and text can never disagree about what the world is doing.
 *
 * Resolution is Mike's default-plus-override (D115): a room carrying its own
 * `ambience` overrides the pack's WHOLESALE; a room carrying none inherits
 * the pack's; a pack carrying none has no audio layer. Within a declaration:
 * first matching variant wins, fallback otherwise — the fourth restatement of
 * the three-line walk, for the same brand-honesty reason the plate resolver
 * states (the payload type differs, and a cast-through-string would trade a
 * brand for a lie).
 *
 * The three answers mean three different things:
 * - an {@link AudioDefinition} — play this bed;
 * - `'silence'` — quiet ON PURPOSE (Calder's §6.2: an authored value the
 *   player experiences as the sound dropping away);
 * - `undefined` — the pack has no audio layer here at all (nothing to fade
 *   from or to; the player never knows the layer exists).
 *
 * @packageDocumentation
 */

import type { AmbienceValue, AudioDefinition, ConditionalAmbience, RuntimeState } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { requireAudio, requireRoom } from '../bundle/bundle-lookups.js';
import { firstMatchingVariant } from '../conditions/first-matching-variant.js';

/** The resolved sound of a room: a bed, authored silence, or no layer at all. */
export type ResolvedAmbience = AudioDefinition | 'silence' | undefined;

/**
 * Resolve one ambience declaration against the live world.
 *
 * @param declaration - The pack's or a room's `ambience`
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @returns The chosen value — an audio id or `'silence'`
 */
const chooseAmbienceValue = (
	declaration: ConditionalAmbience,
	bundle: EngineGameBundle,
	state: RuntimeState,
): AmbienceValue =>
	typeof declaration === 'string'
		? declaration
		: (firstMatchingVariant(declaration.variants, (variant) => variant.condition, bundle, state)?.audio ?? declaration.fallback);

/**
 * Resolve the active room's ambience against the live world.
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @returns The bed to play, `'silence'` (authored quiet), or `undefined` (no audio layer)
 */
export const resolveRoomAmbience = (bundle: EngineGameBundle, state: RuntimeState): ResolvedAmbience => {
	const room = requireRoom(bundle, state.progression.activeRoomId);
	const declaration = room.ambience ?? bundle.contentPack.ambience;

	if (declaration === undefined) {
		return undefined;
	}

	const chosenValue = chooseAmbienceValue(declaration, bundle, state);

	if (chosenValue === 'silence') {
		return 'silence';
	}

	// The loader's audio gate resolves every reference before a bundle exists —
	// a miss here is a corrupt bundle or a fixture lying about its audio.
	return requireAudio(bundle, chosenValue);
};
